import { useQuery } from '@tanstack/react-query';
import { api, type ActivityOutcomesResult, type ApiError } from '@whinerp/client';
import { formatCount, formatDecimal, formatPercent } from '@whinerp/contracts/format';
import { Panel, SectionLabel } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';

/**
 * Which activities came before won deals (G-092), under the coaching grid.
 *
 * Worded as "came before", never "causes", and a rate from too few deals is
 * shown as too few rather than as a percentage.
 */

export function ActivityOutcomesPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const data = useQuery<ActivityOutcomesResult, ApiError>({
    queryKey: ['activity-outcomes'],
    queryFn: () => api.activityOutcomes({ query: {} }),
  });
  if (data.isPending) return null;
  if (data.isError) {
    return (
      <Panel data-region="activity-outcomes">
        <p className="text-dense text-danger-text">{errorText(data.error)}</p>
      </Panel>
    );
  }
  const view = data.data;
  const pct = (value: number | null) => (value === null ? '-' : formatPercent(value, format));
  const few = t('activityOutcomes.tooFew', { values: { n: String(view.minDeals) } });

  return (
    <Panel data-region="activity-outcomes">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('activityOutcomes.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('activityOutcomes.help')}</p>
        {view.types.length === 0 ? (
          <p className="text-body text-ink-meta" data-activity-outcomes-empty>{t('activityOutcomes.none')}</p>
        ) : (
          <div data-scroll-container className="overflow-x-auto">
            <table className="w-full text-dense" data-activity-outcomes>
              <thead>
                <tr className="text-meta text-ink-meta">
                  <th className="p-1 text-start font-normal">{t('activityOutcomes.activity')}</th>
                  <th className="p-1 text-end font-normal">{t('activityOutcomes.withIt')}</th>
                  <th className="p-1 text-end font-normal">{t('activityOutcomes.withoutIt')}</th>
                  <th className="p-1 text-end font-normal">{t('activityOutcomes.beforeWin')}</th>
                  <th className="p-1 text-end font-normal">{t('activityOutcomes.beforeLoss')}</th>
                </tr>
              </thead>
              <tbody>
                {view.types.map((row) => (
                  <tr key={row.type} data-activity-outcome={row.type}>
                    <td className="p-1 text-ink">{row.label}</td>
                    {row.enough ? (
                      <>
                        <td className="p-1 text-end text-ink" data-with-rate>{pct(row.withIt.winRate)}</td>
                        <td className="p-1 text-end text-ink" data-without-rate>{pct(row.withoutIt.winRate)}</td>
                      </>
                    ) : (
                      <td colSpan={2} className="p-1 text-end text-ink-meta" data-too-few>
                        {few} ({formatCount(row.withIt.won + row.withIt.lost, format)})
                      </td>
                    )}
                    <td className="p-1 text-end text-ink">
                      {row.medianBeforeWin === null ? '-' : formatDecimal(String(row.medianBeforeWin), format)}
                    </td>
                    <td className="p-1 text-end text-ink">
                      {row.medianBeforeLoss === null ? '-' : formatDecimal(String(row.medianBeforeLoss), format)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Panel>
  );
}
